import { Check, KeyRound, Pencil, X } from 'lucide-react';
import styled from 'styled-components';

import { Badge } from '../../../../components/atoms/badge/badge';
import { Button } from '../../../../components/atoms/button/button';
import { Spinner } from '../../../../components/atoms/spinner/spinner';
import { Drawer } from '../../../../components/molecules/drawer/drawer';
import { ErrorState } from '../../../../components/molecules/error-state/error-state';
import { groupByModule, moduleLabel } from '../../../../types/permission';
import { UsuarioInterno } from '../../../../types/usuario';
import { formatDate, formatDateTime } from './format';
import { UserStatusBadge } from './user-status-badge';

interface Props {
  isOpen: boolean;
  detail: UsuarioInterno | null;
  status: 'idle' | 'loading' | 'error';
  errorMessage?: string | null;
  isSelf: boolean;
  canUpdate: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
  canEditPermissions: boolean;
  onClose: () => void;
  onEdit: () => void;
  onEditPermissions: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  h3 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;

  span:first-child {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ModuleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  h4 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Empty = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FooterHelp = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: right;
  flex: 1;
`;

export const UserDetailDrawer = ({
  isOpen,
  detail,
  status,
  errorMessage,
  isSelf,
  canUpdate,
  canActivate,
  canDeactivate,
  canEditPermissions,
  onClose,
  onEdit,
  onEditPermissions,
  onActivate,
  onDeactivate,
}: Props) => {
  const grouped = detail ? groupByModule(
    detail.permissions.map((key) => ({ id: key, key, module: keyModule(key), description: null })),
  ) : {};

  const deactivateDisabled = isSelf;
  const deactivateTitle = isSelf
    ? 'Você não pode desativar a si mesmo'
    : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do usuário"
      description={detail?.email}
      footer={
        detail ? (
          <>
            {isSelf && (
              <FooterHelp>Você está visualizando sua própria conta</FooterHelp>
            )}
            {detail.active && canDeactivate && (
              <Button
                variant="secondary"
                onClick={onDeactivate}
                disabled={deactivateDisabled}
                title={deactivateTitle}
                startIcon={<X size={14} />}
                data-testid="open-deactivate-modal"
              >
                Desativar
              </Button>
            )}
            {!detail.active && canActivate && (
              <Button
                variant="primary"
                onClick={onActivate}
                startIcon={<Check size={14} />}
                data-testid="open-activate-modal"
              >
                Ativar
              </Button>
            )}
            {canEditPermissions && (
              <Button
                variant="primary"
                onClick={onEditPermissions}
                startIcon={<KeyRound size={14} />}
                data-testid="open-edit-permissions-modal"
              >
                Editar permissões
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="secondary"
                onClick={onEdit}
                startIcon={<Pencil size={14} />}
                data-testid="open-edit-user-modal"
              >
                Editar dados
              </Button>
            )}
          </>
        ) : null
      }
    >
      {status === 'loading' && (
        <Loading>
          <Spinner /> Carregando detalhes…
        </Loading>
      )}

      {status === 'error' && (
        <ErrorState
          title="Não foi possível carregar o usuário"
          description={errorMessage ?? 'Tente novamente mais tarde.'}
        />
      )}

      {detail && status !== 'loading' && (
        <>
          <Section>
            <StatusRow>
              <Title>{detail.name}</Title>
              <UserStatusBadge active={detail.active} />
            </StatusRow>
            <Row>
              <span>E-mail</span>
              <span>{detail.email}</span>
            </Row>
            <Row>
              <span>Criado em</span>
              <span>{formatDate(detail.createdAt)}</span>
            </Row>
            <Row>
              <span>Último login</span>
              <span>{formatDateTime(detail.lastLoginAt)}</span>
            </Row>
          </Section>

          <Section>
            <h3>Permissões ({detail.permissions.length})</h3>
            {detail.permissions.length === 0 ? (
              <Empty>Este usuário não tem permissões atribuídas.</Empty>
            ) : (
              Object.entries(grouped).map(([mod, perms]) => (
                <ModuleBlock key={mod}>
                  <h4>{moduleLabel(mod)}</h4>
                  <BadgeRow>
                    {perms.map((p) => (
                      <Badge key={p.key} variant="info">
                        {p.key}
                      </Badge>
                    ))}
                  </BadgeRow>
                </ModuleBlock>
              ))
            )}
          </Section>
        </>
      )}
    </Drawer>
  );
};

const keyModule = (key: string): string => {
  if (key.startsWith('USER_')) return 'usuarios';
  if (key.startsWith('SAQUE_')) return 'saques';
  return 'outros';
};
